'use client';

import type { INavListItem } from '@/api/types/public';
import { useLayoutEffect, useRef, useState } from 'react';
import {
  canDisplayResourceSlots,
  resolveVisibleResourceSlotCount,
} from './navigationLayoutPriority';
import ResourceSlotNav from './ResourceSlotNav.pc';
import styles from './index.module.scss';
import TextNav from './TextNav';

interface TextResourceNavigationLayoutProps {
  textNavItems: INavListItem[];
  resourceSlotItems: INavListItem[];
  firstLevelIndex: number;
  firstLevelTitle: string;
  isDarkMode: boolean;
  isActive: boolean;
}

const RESPONSIVE_DESKTOP_QUERY = '(min-width: 1281px) and (max-width: 1919px)';
const WIDE_DESKTOP_QUERY = '(min-width: 1920px)';

const TextResourceNavigationLayout = ({
  textNavItems,
  resourceSlotItems,
  firstLevelIndex,
  firstLevelTitle,
  isDarkMode,
  isActive,
}: TextResourceNavigationLayoutProps) => {
  const layoutRef = useRef<HTMLDivElement>(null);
  const textNavigationRef = useRef<HTMLDivElement>(null);
  const [visibleResourceSlotCount, setVisibleResourceSlotCount] = useState(
    resourceSlotItems.length,
  );
  const textNavItemCount = textNavItems.length;
  const resourceSlotCount = resourceSlotItems.length;

  useLayoutEffect(() => {
    if (!isActive) return;

    const updateResourceSlotVisibility = () => {
      const layout = layoutRef.current;
      const textNavigation = textNavigationRef.current;
      if (!layout || !textNavigation) return;

      const isResponsiveDesktop = window.matchMedia(RESPONSIVE_DESKTOP_QUERY).matches;
      const isWideDesktop = window.matchMedia(WIDE_DESKTOP_QUERY).matches;
      const nextVisibleResourceSlotCount = isWideDesktop
        ? resolveVisibleResourceSlotCount({
            availableWidth: layout.clientWidth,
            higherPriorityWidth: textNavigation.scrollWidth,
            resourceSlotCount,
          })
        : !isResponsiveDesktop ||
            canDisplayResourceSlots({
              availableWidth: layout.clientWidth,
              higherPriorityWidth: textNavigation.scrollWidth,
              resourceSlotCount,
            })
          ? resourceSlotCount
          : 0;

      setVisibleResourceSlotCount((currentVisibleResourceSlotCount) =>
        currentVisibleResourceSlotCount === nextVisibleResourceSlotCount
          ? currentVisibleResourceSlotCount
          : nextVisibleResourceSlotCount,
      );
    };

    updateResourceSlotVisibility();

    const resizeObserver = new ResizeObserver(updateResourceSlotVisibility);
    if (layoutRef.current) resizeObserver.observe(layoutRef.current);
    if (textNavigationRef.current) resizeObserver.observe(textNavigationRef.current);

    return () => resizeObserver.disconnect();
  }, [isActive, resourceSlotCount, textNavItemCount]);

  const visibleResourceSlotItems = resourceSlotItems.slice(0, visibleResourceSlotCount);

  return (
    <div ref={layoutRef} className={styles.textResourceNavigationLayout}>
      <div
        ref={textNavigationRef}
        className={`flex items-start ${styles.textResourceNavigationTextContainer}`}
      >
        {textNavItems.map((item, index) => (
          <div
            key={`${item.id}-${item.template_id}`}
            className={styles.responsiveTextNavigationColumn}
          >
            <TextNav
              item={item}
              firstLevelIndex={firstLevelIndex}
              firstLevelTitle={firstLevelTitle}
              secondLevelIndex={index}
              isLast={index === textNavItems.length - 1}
              isDarkMode={isDarkMode}
            />
          </div>
        ))}
      </div>

      {visibleResourceSlotItems.length > 0 ? (
        <div
          className={`ml-auto flex shrink-0 ${styles.resourceSlotContainer} ${styles.textResourceNavigationResourceContainer}`}
        >
          {visibleResourceSlotItems.map((item, resourceIndex) => (
            <ResourceSlotNav
              key={`${item.id}-${item.template_id}`}
              item={item}
              firstLevelIndex={firstLevelIndex}
              firstLevelTitle={firstLevelTitle}
              resourceIndex={resourceIndex}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default TextResourceNavigationLayout;
